// @ts-check
"use strict"

import { char, choice, endOfInput, many, possibly, recursiveParser, regex, sequenceOf, str } from "arcsecond"

/** @param {string} text */
export function escapeSmileMarkdownCharacters(text) {
  let escaped = ""
  for (let word of text.split(" ")) {
    if(escaped !== "") escaped += " "

    if(isValidSmile(word)) escaped += word.replace(/([\[\]\(\)])/g, "\\$1")
    else escaped += word
  }

  return escaped
}

/**
  * @param {string} smile
  */
function isValidSmile(smile) {
  const $fullSmile = sequenceOf([
    $smile,
    endOfInput,
  ])
  const result = $fullSmile.run(smile)
  return !result.isError
}


/*
  * ******************************
  * ******************************
  *
  * SMILES PARSER
  *
  * ******************************
  *
  * @see https://github.com/reymond-group/smilesDrawer/blob/master/peg/smiles.pegjs
  *
  * ******************************
  * ******************************
  */

// chain = s:(atom branch* (bond? ring)* branch* bond? chain? branch*)
/** @type {import('arcsecond').Parser<*>} */
const $smile = recursiveParser(() => (
  sequenceOf([
    $atom,
    many($branch),
    many(sequenceOf([
      possibly($bond),
      $ring
    ])),
    many($branch),
    possibly($bond),
    possibly($smile),
    many($branch),
  ])
));

// bond = b:([-=#$:/\\.])
const $bond = regex(/^[-=#$:\/.\\]/);

// ring = r:('%'[1-9][0-9] / [0-9])
const $ring = choice([
  sequenceOf([char("%"), regex(/^[1-9][0-9]/)]),
  regex(/^[0-9]/),
]);

// organicsymbol = o:('B''r'? / 'C''l'? / [NOPSFI])
const $organicSymbol = choice([
  sequenceOf([str("B"), possibly(str("r"))]),
  sequenceOf([str("C"), possibly(str("l"))]),
  regex(/^[NOPSFI]/),
]);

// aromaticsymbol = a:([bcnops])
const $aromaticSymbol = regex(/^[bcnops]/);

// wildcard = w:('*')
const $wildcard = str("*");

// elementsymbol = e:('Ac' / 'Ag' / 'Al' / 'Am' / 'Ar' / 'As' / 'At' / 'Au' /
// 'B'[aehikr]? /
// 'C'[adeflmnorsu]? /
// 'Db' / 'Ds' / 'Dy' /
// 'Er' / 'Es' / 'Eu' /
// 'F'[elmr]? /
// 'Ga' / 'Ge' / 'Gd' / 'Ge' /
// 'H'[efgos]? /
// 'I'[nr]? /
// 'K'[r]? /
// 'La' / 'Li' / 'Lr' / 'Lu' / 'Lv' /
// 'Mc' / 'Md' / 'Mg' / 'Mn' / 'Mo' / 'Mt' /
// 'N'[abdehiop]? /
// 'O'[gs]? /
// 'P'[abdmortu]? /
// 'Ra' / 'Rb' / 'Re' / 'Rf' / 'Rg' / 'Rh' / 'Rn' / 'Ru' /
// 'S'[bcegimnr]? /
// 'Ta' / 'Tb' / 'Tc' / 'Te' / 'Th' / 'Ti' / 'Tl' / 'Tm' / 'Ts' /
// 'U' /
// 'V' /
// 'W' /
// 'Xe' /
// 'Y''b'? /
// 'Zn' / 'Zr')
const $elementSymbol = choice([
  str("Ac"), str("Ag"), str("Al"), str("Am"), str("Ar"), str("As"), str("At"), str("Au"),
  sequenceOf([str("B"), possibly(regex(/^[aehikr]/))]),
  sequenceOf([str("C"), possibly(regex(/^[adeflmnorsu]/))]),
  str("Db"), str("Ds"), str("Dy"),
  str("Er"), str("Es"), str("Eu"),
  sequenceOf([str("F"), possibly(regex(/^[elmr]/))]),
  str("Ga"), str("Ge"), str("Gd"), str("Ge"),
  sequenceOf([str("H"), possibly(regex(/^[efgos]/))]),
  sequenceOf([str("I"), possibly(regex(/^[nr]/))]),
  sequenceOf([str("K"), possibly(regex(/^[r]/))]),
  str("La"), str("Li"), str("Lr"), str("Lu"), str("Lv"),
  str("Mc"), str("Md"), str("Mg"), str("Mn"), str("Mo"), str("Mt"),
  sequenceOf([str("N"), possibly(regex(/^[abdehiop]/))]),
  sequenceOf([str("O"), possibly(regex(/^[gs]/))]),
  sequenceOf([str("P"), possibly(regex(/^[abdmortu]/))]),
  str("Ra"), str("Rb"), str("Re"), str("Rf"), str("Rg"), str("Rh"), str("Rn"), str("Ru"),
  sequenceOf([str("S"), possibly(regex(/^[bcegimnr]/))]),
  str("Ta"), str("Tb"), str("Tc"), str("Te"), str("Th"), str("Ti"), str("Tl"), str("Tm"), str("Ts"),
  str("U"),
  str("V"),
  str("W"),
  str("Xe"),
  sequenceOf([str("Y"), possibly(str("b"))]),
  str("Zn"), str("Zr")
]);

// chiral = c:('@'('@' / 'TH'[12] / ''[12] / 'SP'[1-3] / 'TB'[1-9][0-9]? / 'OH'[1-9][0-9]?)?)
const $chiral = sequenceOf([
  char("@"),
  possibly(choice([
    char("@"),
    regex(/^TH[12]/),
    regex(/^[12]/),
    regex(/^SP[1-3]/),
    regex(/^TB[1-9][0-9]?/),
    regex(/^OH[1-9][0-9]?/),
  ])),
]);

// poscharge = c:('+'('+' / [1-9][0-9]?)?)
const $posCharge = sequenceOf([
  char("+"),
  possibly(choice([
    char("+"),
    regex(/^[1-9][0-9]?/),
  ])),
]);

// negcharge = c:('-'('-' / [1-9][0-9]?)?)
const $negCharge = sequenceOf([
  char("-"),
  possibly(choice([
    char("-"),
    regex(/^[1-9][0-9]?/),
  ])),
]);

// charge = c:(poscharge / negcharge)
const $charge = choice([$posCharge, $negCharge]);

// hcount = h:('H'[0-9]?)
const $hcount = sequenceOf([
  char("H"),
  possibly(regex(/^[0-9]/)),
]);

// class = c:(':'([1-9][0-9]* / [0]))
const $class = sequenceOf([
  char(":"),
  choice([
    regex(/^[1-9][0-9]*/),
    char("0"),
  ]),
]);

// isotope = i:([1-9][0-9]?[0-9]?)
const $isotope = regex(/^[1-9][0-9]?[0-9]?/);

// bracketcontent = b:(isotope? ('se' / 'as' / aromaticsymbol / elementsymbol / wildcard) chiral? hcount? charge? class?)
const $bracketContent = sequenceOf([
  possibly($isotope),
  choice([
    str("se"),
    str("as"),
    $aromaticSymbol,
    $elementSymbol,
    $wildcard,
  ]),
  possibly($chiral),
  possibly($hcount),
  possibly($charge),
  possibly($class),
]);

// bracketatom = '[' b:bracketcontent ']'
const $bracketom = sequenceOf([
  char("["),
  $bracketContent,
  char("]"),
]);

// branch = b:('(' bond? chain ')')
const $branch = sequenceOf([
  char("("),
  possibly($bond),
  $smile,
  char(")"),
]);

// atom = a:(organicsymbol / aromaticsymbol / bracketatom / wildcard)
const $atom = choice([
  $organicSymbol,
  $aromaticSymbol,
  $bracketom,
  $wildcard,
]);
